import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { VenueSnapshot } from '@/types/franchise';
import { Building2, Star, Users, MapPin, Clock, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';

interface VenueStatusGridProps {
  venues: VenueSnapshot[];
  loading?: boolean;
  onVenueClick: (businessId: string) => void;
  showDetails?: boolean;
}

interface VenueCardProps {
  venue: VenueSnapshot;
  onClick: () => void;
  showDetails?: boolean;
}

const VenueCard: React.FC<VenueCardProps> = ({ venue, onClick, showDetails }) => {
  const isMobile = useIsMobile();

  const statusColors = {
    healthy: 'bg-green-500',
    warning: 'bg-orange-500',
    critical: 'bg-red-500',
  };

  const statusBadgeVariants = {
    healthy: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
    warning: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
    critical: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  };

  const scheduleStatusColors = {
    green: 'text-green-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
  };

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all hover:shadow-md hover:border-primary/50",
        "group relative overflow-hidden"
      )}
      onClick={onClick}
    >
      {/* Status indicator bar */}
      <div className={cn("absolute top-0 left-0 right-0 h-1", statusColors[venue.status])} />

      <CardContent className={cn("pt-4", isMobile ? "p-3" : "p-4")}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-base truncate">{venue.businessName}</h3>
              {venue.isOpen ? (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300">
                  Open
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900 dark:text-gray-400">
                  Closed
                </Badge>
              )}
            </div>

            {venue.address && showDetails && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 truncate">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                {venue.address}
              </p>
            )}
          </div>

          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
        </div>

        {/* Quick stats */}
        <div className={cn(
          "mt-3 grid gap-2",
          isMobile ? "grid-cols-2" : "grid-cols-3"
        )}>
          {/* Rating */}
          <div className="flex items-center gap-1.5">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-medium">
              {venue.averageRating > 0 ? venue.averageRating.toFixed(1) : '-'}
            </span>
            <span className="text-xs text-muted-foreground">
              ({venue.totalReviews})
            </span>
          </div>

          {/* Employees */}
          <div className="flex items-center gap-1.5">
            <Users className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">{venue.employeeCount}</span>
            <span className="text-xs text-muted-foreground">staff</span>
          </div>

          {/* On shift */}
          <div className={cn(
            "flex items-center gap-1.5",
            isMobile ? "col-span-2" : ""
          )}>
            <Clock className={cn("h-4 w-4", scheduleStatusColors[venue.scheduleStatus])} />
            <span className="text-sm font-medium">{venue.employeesOnShift}</span>
            <span className="text-xs text-muted-foreground">on shift</span>
          </div>
        </div>

        {/* Status badge */}
        <div className="mt-3 flex items-center justify-between">
          <Badge className={cn("text-xs", statusBadgeVariants[venue.status])}>
            {venue.status === 'healthy' ? 'All Good' :
             venue.status === 'warning' ? 'Needs Attention' :
             'Critical'}
          </Badge>

          {venue.scheduleStatus === 'red' && (
            <span className="text-xs text-red-600">Unassigned shifts</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const VenueStatusGrid: React.FC<VenueStatusGridProps> = ({
  venues,
  loading,
  onVenueClick,
  showDetails = false,
}) => {
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <div className={cn(
            "grid gap-4",
            isMobile ? "grid-cols-1" : "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          )}>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-40 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (venues.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No venues found</p>
        </CardContent>
      </Card>
    );
  }

  // Sort venues: critical first, then warning, then healthy
  const sortedVenues = [...venues].sort((a, b) => {
    const statusOrder = { critical: 0, warning: 1, healthy: 2 };
    return statusOrder[a.status] - statusOrder[b.status];
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            Venues
          </span>
          <span className="text-sm font-normal text-muted-foreground">
            {venues.length} total
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn(
          "grid gap-4",
          isMobile ? "grid-cols-1" : "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        )}>
          {sortedVenues.map(venue => (
            <VenueCard
              key={venue.businessId}
              venue={venue}
              onClick={() => onVenueClick(venue.businessId)}
              showDetails={showDetails}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default VenueStatusGrid;
